import classNames from "classnames";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Flex } from "../../flex";
import type { RedoButtonSize } from "../buttons/redo-button";
import * as styles from "./redo-button-tab-group.module.css";

export const redoButtonTabGroupVariant = ["raised", "flat"] as const;
export type RedoButtonTabGroupVariant =
  (typeof redoButtonTabGroupVariant)[number];

/** Text nodes miss the button's `> *` truncation styles, so wrap them. */
function renderTabContent(el: ReactNode): ReactNode {
  if (typeof el === "string" || typeof el === "number") {
    return <span>{el}</span>;
  }
  return el;
}

export const RedoButtonTabGroup = <T,>({
  value,
  onChange,
  options,
  size = "md",
  disabled = false,
  fullWidth = true,
  tabWidth = "equal",
  variant = "raised",
  className,
  "data-dd-action-name": actionName,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; el: ReactNode; "data-dd-action-name"?: string }[];
  size?: Extract<RedoButtonSize, "xs" | "sm" | "md" | "lg">;
  disabled?: boolean;
  fullWidth?: boolean;
  /**
   * With `fullWidth`, `content` splits the spare width on top of each label's
   * own width, so a long label doesn't truncate beside short ones.
   */
  tabWidth?: "equal" | "content";
  /**
   * Use `raised` for standalone controls. Use `flat` when the control is nested
   * in a bordered surface that already provides its visual boundary.
   */
  variant?: RedoButtonTabGroupVariant;
  className?: string;
  "data-dd-action-name"?: string;
}) => {
  const refGroup = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);
  const [animate, setAnimate] = useState(false);

  const activeIndex = options.findIndex((option) => option.value === value);

  // Measure the active button rather than assuming equal-width tabs, so the
  // indicator is correct for content-sized tabs (fullWidth={false}) too.
  useLayoutEffect(() => {
    const group = refGroup.current;
    const activeButton = buttonRefs.current[activeIndex];
    if (!group || !activeButton) {
      setIndicator(null);
      return;
    }

    const measure = () => {
      const groupRect = group.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicator({
        left: buttonRect.left - groupRect.left - group.clientLeft,
        width: buttonRect.width,
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(group);
    for (const button of buttonRefs.current) {
      if (button) {
        resizeObserver.observe(button);
      }
    }
    return () => resizeObserver.disconnect();
  }, [activeIndex, options.length, size, fullWidth, tabWidth]);

  // Enable the slide transition only after the first measured position so the
  // indicator doesn't animate in from the corner on mount.
  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Flex
      className={classNames(
        styles.redoButtonTabGroup,
        styles[size],
        disabled && styles.disabled,
        fullWidth && styles.fullWidth,
        fullWidth && tabWidth === "content" && styles.contentWidthTabs,
        variant === "flat" && styles.flat,
        className,
      )}
      data-arbiter="RedoButtonTabGroup"
      data-dd-action-name={actionName}
      gap="none"
      p={size === "xs" ? "xxs" : "xs"}
      ref={refGroup}
    >
      <div
        className={classNames(
          styles.activeIndicator,
          animate && styles.animate,
        )}
        style={
          indicator
            ? { left: indicator.left, width: indicator.width }
            : { opacity: 0 }
        }
      />
      <div className={styles.tabButtonsContainer}>
        {options.map((option, index) => (
          <button
            aria-pressed={option.value === value}
            className={classNames(
              styles.tabButton,
              option.value === value && styles.active,
            )}
            data-dd-action-name={option["data-dd-action-name"]}
            disabled={disabled}
            key={index}
            onClick={(e) => {
              e.preventDefault();
              if (!disabled) {
                onChange(option.value);
              }
            }}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
          >
            {renderTabContent(option.el)}
          </button>
        ))}
      </div>
    </Flex>
  );
};
